import { z } from "zod/v4";

import { SqlString } from "../SqlString";

export const sqliteColumnTypeSchema = z.union([
	z.enum(["INTEGER", "REAL", "TEXT", "BLOB", "BOOLEAN", "DATE", "DATETIME"]),
	z.undefined(),
]);
export type SqliteColumnType = z.infer<typeof sqliteColumnTypeSchema>;

const typeInfoText = z.object({
	enrichedType: z.literal("Text"),
	defaultValue: z.string().optional(),
});
export type TypeInfoText = z.infer<typeof typeInfoText>;

const typeInfoReaction = z.object({ enrichedType: z.literal("Reaction") });
export type TypeInfoReaction = z.infer<typeof typeInfoReaction>;

const colorVariantOptions = z.union([
	z.literal("default"), // gray
	z.literal("red"),
	z.literal("yellow"),
	z.literal("green"),
	z.literal("cyan"),
	z.literal("blue"),
	z.literal("purple"),
	z.literal("pink"),
]);

const typeInfoSelect = z.object({
	enrichedType: z.literal("Select"),
	options: z.array(z.object({ label: z.string(), color: colorVariantOptions })).optional(),
	defaultValue: z.string().optional(),
});
export type TypeInfoSelect = z.infer<typeof typeInfoSelect>;

export const personDefaultValues = ["Empty", "Current user"] as const;
const personDefaultValuesSchema = z.enum(personDefaultValues);

const typeInfoPerson = z.object({
	enrichedType: z.literal("Person"),
	defaultValue: personDefaultValuesSchema.optional(),
});
export type TypeInfoPerson = z.infer<typeof typeInfoPerson>;

const typeInfo = z.discriminatedUnion("enrichedType", [
	typeInfoText,
	typeInfoReaction,
	typeInfoSelect,
	typeInfoPerson,
]);
export type TypeInfo = z.infer<typeof typeInfo>;

export const sqliteColumnDefSchema = z.object({
	name: z.string(),
	type: sqliteColumnTypeSchema,
	typeInfo: typeInfo.optional(),
	header: z.string().optional(),
	nullable: z.boolean().default(true).optional(),
	primaryKey: z.boolean().default(false).optional(),
	unique: z.boolean().default(false).optional(),
	defaultValue: z
		.union([z.string(), z.number(), z.boolean(), z.null(), z.instanceof(SqlString)])
		.optional(),
	references: z
		.object({
			table: z.string(),
			column: z.string(),
		})
		.optional(),
	check: z.string().optional(), // SQL CHECK constraint
});
export type SqliteColumnDef = z.infer<typeof sqliteColumnDefSchema>;

export const sqliteTableDefSchema = z.object({
	tableName: z.string(),
	columns: z.array(sqliteColumnDefSchema),
});

export type SqliteTableDef = z.infer<typeof sqliteTableDefSchema>;
